import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plus, Trash2, GripVertical, Zap, Save } from 'lucide-react'
import { toast } from 'sonner'
import { automationApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  TriggerType,
  ActionType,
  ConditionOperator,
  CreateAutomationRequest,
  UpdateAutomationRequest,
} from '@/types'

const TRIGGER_OPTIONS: { value: TriggerType; label: string; category: string }[] = [
  { value: 'LEAD_CREATED', label: 'Lead Created', category: 'Leads' },
  { value: 'LEAD_STATUS_CHANGED', label: 'Lead Status Changed', category: 'Leads' },
  { value: 'LEAD_ASSIGNED', label: 'Lead Assigned', category: 'Leads' },
  { value: 'LEAD_CONVERTED', label: 'Lead Converted', category: 'Leads' },
  { value: 'DEAL_CREATED', label: 'Deal Created', category: 'Deals' },
  { value: 'DEAL_STAGE_CHANGED', label: 'Deal Stage Changed', category: 'Deals' },
  { value: 'DEAL_WON', label: 'Deal Won', category: 'Deals' },
  { value: 'DEAL_LOST', label: 'Deal Lost', category: 'Deals' },
  { value: 'DEAL_ASSIGNED', label: 'Deal Assigned', category: 'Deals' },
  { value: 'ACTIVITY_CREATED', label: 'Activity Created', category: 'Activities' },
  { value: 'TASK_COMPLETED', label: 'Task Completed', category: 'Tasks' },
  { value: 'TASK_OVERDUE', label: 'Task Overdue', category: 'Tasks' },
  { value: 'REMINDER_DUE', label: 'Reminder Due', category: 'Reminders' },
]

const ACTION_OPTIONS: { value: ActionType; label: string }[] = [
  { value: 'SEND_EMAIL', label: 'Send Email' },
  { value: 'CREATE_TASK', label: 'Create Task' },
  { value: 'UPDATE_FIELD', label: 'Update Field' },
  { value: 'ASSIGN_USER', label: 'Assign User' },
  { value: 'CREATE_REMINDER', label: 'Create Reminder' },
  { value: 'MOVE_STAGE', label: 'Move to Stage' },
  { value: 'ADD_TAG', label: 'Add Tag' },
  { value: 'REMOVE_TAG', label: 'Remove Tag' },
  { value: 'NOTIFY_USER', label: 'Notify User' },
]

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Not Equals' },
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'NOT_CONTAINS', label: 'Does Not Contain' },
  { value: 'GREATER_THAN', label: 'Greater Than' },
  { value: 'LESS_THAN', label: 'Less Than' },
  { value: 'IN', label: 'In List' },
  { value: 'NOT_IN', label: 'Not In List' },
  { value: 'IS_EMPTY', label: 'Is Empty' },
  { value: 'IS_NOT_EMPTY', label: 'Is Not Empty' },
]

interface FormCondition {
  id: string
  field_name: string
  operator: ConditionOperator
  field_value: string
  condition_group: number
}

interface FormAction {
  id: string
  action_type: ActionType
  action_config: Record<string, unknown>
  delay_minutes: number
}

export default function AutomationFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ruleId } = useParams<{ ruleId: string }>()
  const queryClient = useQueryClient()
  const isEditing = !!ruleId && ruleId !== 'new'

  const [ruleName, setRuleName] = useState('')
  const [description, setDescription] = useState('')
  const [triggerType, setTriggerType] = useState<TriggerType>('LEAD_CREATED')
  const [isActive, setIsActive] = useState(true)
  const [conditions, setConditions] = useState<FormCondition[]>([])
  const [actions, setActions] = useState<FormAction[]>([])

  const { data: existingRule, isLoading: isLoadingRule } = useQuery({
    queryKey: ['automation', ruleId],
    queryFn: () => automationApi.getAutomation(ruleId!),
    enabled: isEditing,
  })

  useEffect(() => {
    if (existingRule) {
      setRuleName(existingRule.rule_name)
      setDescription(existingRule.description || '')
      setTriggerType(existingRule.trigger_type)
      setIsActive(existingRule.is_active)
      setConditions(
        (existingRule.conditions || []).map((c: { condition_id?: string; field_name: string; operator: ConditionOperator; field_value: string; condition_group: number }, i: number) => ({
          id: c.condition_id || `cond-${i}`,
          field_name: c.field_name,
          operator: c.operator,
          field_value: c.field_value,
          condition_group: c.condition_group,
        }))
      )
      setActions(
        (existingRule.actions || []).map((a: { action_id?: string; action_type: ActionType; action_config: Record<string, unknown>; delay_minutes: number }, i: number) => ({
          id: a.action_id || `act-${i}`,
          action_type: a.action_type,
          action_config: a.action_config,
          delay_minutes: a.delay_minutes,
        }))
      )
    }
  }, [existingRule])

  const createMutation = useMutation({
    mutationFn: (data: CreateAutomationRequest) => automationApi.createAutomation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] })
      toast.success(t('crm.automations.createSuccess', 'Automation created successfully'))
      navigate('/crm/automations')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateAutomationRequest) => automationApi.updateAutomation(ruleId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automations'] })
      queryClient.invalidateQueries({ queryKey: ['automation', ruleId] })
      toast.success(t('crm.automations.updateSuccess', 'Automation updated successfully'))
      navigate('/crm/automations')
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error))
    },
  })

  const addCondition = () => {
    setConditions([
      ...conditions,
      {
        id: `cond-${Date.now()}`,
        field_name: '',
        operator: 'EQUALS',
        field_value: '',
        condition_group: 0,
      },
    ])
  }

  const removeCondition = (id: string) => {
    setConditions(conditions.filter((c) => c.id !== id))
  }

  const updateCondition = (id: string, updates: Partial<FormCondition>) => {
    setConditions(conditions.map((c) => (c.id === id ? { ...c, ...updates } : c)))
  }

  const addAction = () => {
    setActions([
      ...actions,
      {
        id: `act-${Date.now()}`,
        action_type: 'CREATE_TASK',
        action_config: {},
        delay_minutes: 0,
      },
    ])
  }

  const removeAction = (id: string) => {
    setActions(actions.filter((a) => a.id !== id))
  }

  const updateAction = (id: string, updates: Partial<FormAction>) => {
    setActions(actions.map((a) => (a.id === id ? { ...a, ...updates } : a)))
  }

  const handleSubmit = () => {
    if (!ruleName.trim()) {
      toast.error(t('crm.automations.nameRequired', 'Rule name is required'))
      return
    }
    if (actions.length === 0) {
      toast.error(t('crm.automations.actionsRequired', 'At least one action is required'))
      return
    }

    const data = {
      rule_name: ruleName,
      description: description || undefined,
      trigger_type: triggerType,
      is_active: isActive,
      conditions: conditions.map((c, i) => ({
        field_name: c.field_name,
        operator: c.operator,
        field_value: c.field_value,
        condition_group: c.condition_group,
        position: i,
      })),
      actions: actions.map((a, i) => ({
        action_type: a.action_type,
        action_config: a.action_config,
        delay_minutes: a.delay_minutes,
        position: i,
      })),
    }

    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingRule) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center py-12">{t('common.loading', 'Loading...')}</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/automations')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Zap className="h-8 w-8 text-yellow-500" />
              {isEditing
                ? t('crm.automations.editTitle', 'Edit Automation')
                : t('crm.automations.createTitle', 'Create Automation')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            <Label>{isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}</Label>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </Button>
        </div>
      </div>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t('crm.automations.basicInfo', 'Basic Information')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ruleName">{t('crm.automations.ruleName', 'Rule Name')} *</Label>
              <Input
                id="ruleName"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
                placeholder={t('crm.automations.ruleNamePlaceholder', 'e.g., Welcome email for new leads')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trigger">{t('crm.automations.trigger', 'Trigger')} *</Label>
              <Select value={triggerType} onValueChange={(v) => setTriggerType(v as TriggerType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">{t('common.description', 'Description')}</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('crm.automations.descriptionPlaceholder', 'Describe what this automation does...')}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Conditions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('crm.automations.conditions', 'Conditions')}</CardTitle>
              <CardDescription>
                {t('crm.automations.conditionsDesc', 'Optional: Add conditions to filter when this automation runs')}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addCondition}>
              <Plus className="mr-2 h-4 w-4" />
              {t('crm.automations.addCondition', 'Add Condition')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {conditions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('crm.automations.noConditions', 'No conditions. This automation will run for all matching triggers.')}
            </div>
          ) : (
            <div className="space-y-3">
              {conditions.map((condition) => (
                <div key={condition.id} className="flex items-center gap-3 p-3 border rounded-lg">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                  <Input
                    placeholder={t('crm.automations.fieldName', 'Field name')}
                    value={condition.field_name}
                    onChange={(e) => updateCondition(condition.id, { field_name: e.target.value })}
                    className="w-40"
                  />
                  <Select
                    value={condition.operator}
                    onValueChange={(v) => updateCondition(condition.id, { operator: v as ConditionOperator })}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPERATOR_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder={t('crm.automations.value', 'Value')}
                    value={condition.field_value}
                    onChange={(e) => updateCondition(condition.id, { field_value: e.target.value })}
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeCondition(condition.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('crm.automations.actions', 'Actions')} *</CardTitle>
              <CardDescription>
                {t('crm.automations.actionsDesc', 'Define what happens when this automation is triggered')}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={addAction}>
              <Plus className="mr-2 h-4 w-4" />
              {t('crm.automations.addAction', 'Add Action')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('crm.automations.noActions', 'Add at least one action for this automation.')}
            </div>
          ) : (
            <div className="space-y-3">
              {actions.map((action) => (
                <div key={action.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-move mt-2" />
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <Select
                        value={action.action_type}
                        onValueChange={(v) => updateAction(action.id, { action_type: v as ActionType })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACTION_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-muted-foreground whitespace-nowrap">
                          {t('crm.automations.delayMinutes', 'Delay (min)')}:
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={action.delay_minutes}
                          onChange={(e) => updateAction(action.id, { delay_minutes: parseInt(e.target.value) || 0 })}
                          className="w-20"
                        />
                      </div>
                    </div>
                    {/* Action-specific config would go here */}
                    <ActionConfigEditor
                      actionType={action.action_type}
                      config={action.action_config}
                      onChange={(config) => updateAction(action.id, { action_config: config })}
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeAction(action.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// Action-specific configuration editor
function ActionConfigEditor({
  actionType,
  config,
  onChange,
}: {
  actionType: ActionType
  config: Record<string, unknown>
  onChange: (config: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()

  switch (actionType) {
    case 'SEND_EMAIL':
      return (
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder={t('crm.automations.emailSubject', 'Email subject')}
            value={(config.subject as string) || ''}
            onChange={(e) => onChange({ ...config, subject: e.target.value })}
          />
          <Input
            placeholder={t('crm.automations.emailTemplate', 'Template ID (optional)')}
            value={(config.template_id as string) || ''}
            onChange={(e) => onChange({ ...config, template_id: e.target.value })}
          />
        </div>
      )
    case 'CREATE_TASK':
      return (
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder={t('crm.automations.taskTitle', 'Task title')}
            value={(config.title as string) || ''}
            onChange={(e) => onChange({ ...config, title: e.target.value })}
          />
          <Input
            placeholder={t('crm.automations.dueDays', 'Due in days')}
            type="number"
            value={(config.due_days as number) || ''}
            onChange={(e) => onChange({ ...config, due_days: parseInt(e.target.value) || 0 })}
          />
        </div>
      )
    case 'NOTIFY_USER':
      return (
        <Input
          placeholder={t('crm.automations.notificationMessage', 'Notification message')}
          value={(config.message as string) || ''}
          onChange={(e) => onChange({ ...config, message: e.target.value })}
        />
      )
    case 'ADD_TAG':
    case 'REMOVE_TAG':
      return (
        <Input
          placeholder={t('crm.automations.tagName', 'Tag name')}
          value={(config.tag_name as string) || ''}
          onChange={(e) => onChange({ ...config, tag_name: e.target.value })}
        />
      )
    default:
      return (
        <div className="text-sm text-muted-foreground">
          {t('crm.automations.configureAction', 'Configure action settings as needed')}
        </div>
      )
  }
}

